import React from 'react';
import styled from '@emotion/styled';
import { breakpoints } from '@naf/theme';
import { Button } from '@naf-simba/elements-buttons';
import { OpenInNew } from '@naf-simba/building-blocks-icons/Arrows';
import { simbaStyled } from '@naf-simba/theme';
import { useNavigate } from 'react-router';
import { ExternalLinkType } from '@appTypes/externalLinkType';
import { CtaInternalLinkType } from '@appTypes/internalLinkType';
import useSelector from '@redux/typedHooks';
import { useDocumentUrlWithNode } from '@hooks/Documents/useDocumentUrl';
import { useTrackCTA } from '@utils/Tracking/CtaClick/TrackCtaClick';
import { getHostname } from '@lib/getHostname';
import { resolveLink } from '@lib/resolveLinkProps';

export const SubHeroButtons = ({
  externalLink,
  internalLink,
}: {
  externalLink: ExternalLinkType;
  internalLink: CtaInternalLinkType;
}) => {
  const baseUrl = useSelector((state) => state.application.baseUrl);
  const navigate = useNavigate();
  const trackCTA = useTrackCTA();
  const internalItemUrl = useDocumentUrlWithNode(internalLink.referenceComponent);

  const handleExternalTracking = () => {
    const urlObject = new URL(externalLink.href);
    trackCTA({
      cta_position: 'subHero',
      text: externalLink.text,
      url: externalLink.href,
      cta_hostname: urlObject.hostname,
    });
  };

  const handleInternalTracking = () => {
    trackCTA({
      cta_position: 'subHero',
      text: internalLink.text,
      url: internalItemUrl,
      cta_hostname: getHostname(baseUrl),
    });
  };

  const { isInternal, linkProps } = resolveLink({ url: externalLink.href, baseUrl });

  return (
    <ButtonWrapper>
      {externalLink && (
        <StyledButton
          href={externalLink.link}
          endIcon={OpenInNew}
          onClick={() => {
            handleExternalTracking();
            if (!isInternal) {
              window.open(externalLink.href);
            } else {
              navigate(linkProps.to);
            }
          }}
        >
          {externalLink.text}
        </StyledButton>
      )}
      {internalLink && (
        <StyledButton
          variant="outlined"
          onClick={() => {
            handleInternalTracking();
            navigate(internalItemUrl);
          }}
        >
          {internalLink.text}
        </StyledButton>
      )}
    </ButtonWrapper>
  );
};

const ButtonWrapper = styled.div`
  margin-bottom: 80px;
  @media (max-width: ${breakpoints.l}) {
    width: 100%;
    margin-bottom: 8px;
  }
`;

const StyledButton = simbaStyled(Button)(({ theme }) => ({
  '&&': {
    marginRight: theme.spacing(10),
    [`@media (max-width: ${breakpoints.l})`]: {
      width: '100%',
      marginRight: 0,
      marginBottom: theme.spacing(4),
    },
  },
}));
